import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import gsap from 'gsap'
import { Copy, Check, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TiltCard } from './tilt-card'
import { AnimatedCounter } from './animated-counter'
import { DiffractionSpiral } from './diffraction-spiral'

export interface WalletCardProps {
  balance: number
  subscriptionValue?: number
  isSubscriber?: boolean
  referralCode?: string
  onRedeem?: () => void
  isLoading?: boolean
}

/**
 * Apple Card-inspired Wallet (Light Mode)
 * - Mesh gradient suave
 * - Tipografia escura em fundo claro
 * - Clean e minimal
 */
export function WalletCard({
  balance,
  subscriptionValue = 0,
  isSubscriber = false,
  referralCode = '',
  onRedeem,
  isLoading = false,
}: WalletCardProps) {
  const [copied, setCopied] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const canCoverSubscription = isSubscriber && balance >= subscriptionValue
  const progress =
    isSubscriber && subscriptionValue > 0 ? Math.min((balance / subscriptionValue) * 100, 100) : 0

  const handleCopy = async () => {
    if (!referralCode) return
    await navigator.clipboard.writeText(referralCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleRedeem = () => {
    if (!buttonRef.current || isLoading) return
    gsap.fromTo(
      buttonRef.current,
      { scale: 0.97 },
      { scale: 1, duration: 0.3, ease: 'elastic.out(1, 0.4)' }
    )
    onRedeem?.()
  }

  return (
    <div className="w-full max-w-[400px] mx-auto">
      <TiltCard>
        <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl shadow-black/50">
          {/* Card Face - Deep Black with Holographic Effect */}
          <div className="relative h-[220px] bg-black">
            {/* Holographic diffraction spiral */}
            <DiffractionSpiral className="opacity-100" />

            {/* Brand mark */}
            <div className="absolute top-6 left-6 z-10">
              <span className="text-white/90 text-lg font-semibold tracking-tight">rafobase</span>
            </div>

            {/* Balance on card */}
            <div className="absolute top-6 right-6 z-10">
              <AnimatedCounter
                value={balance}
                className="text-white text-3xl font-bold tracking-tight"
              />
            </div>

            {/* Free month badge */}
            <AnimatePresence>
              {canCoverSubscription && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="absolute bottom-6 right-6 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/10 text-xs font-semibold text-white"
                >
                  1 Mês Grátis
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Card Content - Dark Theme */}
          <div className="px-6 pb-6 pt-2 space-y-5 bg-zinc-900/50 backdrop-blur-xl border-t border-white/5">
            {/* Progress bar */}
            {isSubscriber && progress > 0 && progress < 100 && (
              <div className="space-y-2">
                <div className="flex justify-between text-[11px] text-zinc-400 font-medium">
                  <span>Próximo mês grátis</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                  />
                </div>
              </div>
            )}

            {/* Subscription info */}
            {isSubscriber && subscriptionValue > 0 && (
              <div className="flex justify-between items-center py-2">
                <span className="text-[13px] text-zinc-400">Assinatura</span>
                <span className="text-[13px] text-white font-medium">
                  {subscriptionValue.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </span>
              </div>
            )}

            {/* Referral code */}
            {referralCode && (
              <button
                onClick={handleCopy}
                className={cn(
                  'w-full flex items-center justify-between p-4 rounded-xl',
                  'bg-white/5 hover:bg-white/10 transition-colors duration-200',
                  'group border border-white/5'
                )}
              >
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 font-medium">
                    Seu código
                  </p>
                  <p className="font-mono text-[16px] font-bold text-white tracking-widest">
                    {referralCode}
                  </p>
                </div>
                <div
                  className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center',
                    'bg-white/10 group-hover:bg-white/20 transition-colors',
                    copied && 'bg-emerald-500/20 text-emerald-400'
                  )}
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4 text-zinc-400 group-hover:text-white" />
                  )}
                </div>
              </button>
            )}

            {/* CTA */}
            {balance > 0 && onRedeem && (
              <button
                ref={buttonRef}
                onClick={handleRedeem}
                disabled={isLoading}
                className={cn(
                  'w-full h-12 rounded-xl font-bold text-[15px]',
                  'bg-white text-black',
                  'hover:bg-zinc-200 active:scale-[0.98]',
                  'transition-all duration-200',
                  'flex items-center justify-center gap-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-white/5'
                )}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    Usar Saldo
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </TiltCard>
    </div>
  )
}
